// Reglas de menores por tour y por edad. Función pura: el servidor la usa para cobrar y la pantalla para explicar el precio.
// Cada tour define rangos de edad (por ejemplo 0–4 cortesía, 5–11 monto fijo). Un menor de cortesía puede viajar en piernas y no ocupar lugar.
// Quien supera el último rango (o cae en un hueco) paga como adulto. Sin rangos configurados rige el precio de menor de siempre.
import { z } from "zod";

export const EDAD_MAXIMA = 17;
export const TIPOS_TRAMO = ["cortesia", "monto", "porcentaje"] as const; // porcentaje = % del precio de adulto
export type TipoTramo = (typeof TIPOS_TRAMO)[number];
export type Tramo = { desde: number; hasta: number; tipo: TipoTramo; valor: number; ocupaLugar: boolean };
export type Menor = { edad: number; centavos: number; cortesia: boolean; ocupaLugar: boolean; comoAdulto: boolean };

export const entradaTramos = z.array(z.object({
  desde: z.number().int().min(0).max(EDAD_MAXIMA), hasta: z.number().int().min(0).max(EDAD_MAXIMA),
  tipo: z.enum(TIPOS_TRAMO), valor: z.number().int().min(0).max(1e8), ocupaLugar: z.boolean(),
}).strict()).max(8);
export const entradaEdades = z.array(z.number().int().min(0).max(EDAD_MAXIMA)).max(30);

/** Sin rangos configurados: todos los menores (0 a 11) pagan el precio de menor de siempre y ocupan lugar. */
export const tramosPorDefecto = (ninoCentavos: number): Tramo[] => [{ desde: 0, hasta: 11, tipo: "monto", valor: ninoCentavos, ocupaLugar: true }];

/** Devuelve los problemas de una lista de rangos (vacía si está bien). */
export function problemasDeTramos(tramos: Tramo[]): string[] {
  const p: string[] = [], ord = [...tramos].sort((a, b) => a.desde - b.desde);
  for (const t of tramos) {
    if (t.hasta < t.desde) p.push(`El rango ${t.desde}–${t.hasta} termina antes de empezar`);
    if (t.tipo === "porcentaje" && t.valor > 100) p.push("Un porcentaje no puede pasar de 100");
    if (t.tipo === "cortesia" && t.valor !== 0) p.push("Un rango de cortesía no lleva monto");
  }
  for (let i = 1; i < ord.length; i++) if (ord[i].desde <= ord[i - 1].hasta) p.push(`Los rangos ${ord[i - 1].desde}–${ord[i - 1].hasta} y ${ord[i].desde}–${ord[i].hasta} se empalman`);
  return p;
}

const centavos = (n: number) => Math.max(0, Math.round(n));
/** Qué le toca a un menor de esta edad. */
export function precioMenor(edad: number, tramos: Tramo[], adultoCentavos: number): Menor {
  const t = tramos.find((x) => edad >= x.desde && edad <= x.hasta);
  if (!t) return { edad, centavos: adultoCentavos, cortesia: false, ocupaLugar: true, comoAdulto: true };
  const c = t.tipo === "cortesia" ? 0 : t.tipo === "monto" ? centavos(t.valor) : centavos((adultoCentavos * t.valor) / 100);
  return { edad, centavos: c, cortesia: c === 0, ocupaLugar: t.ocupaLugar, comoAdulto: false };
}

export type CotizacionMenores = { detalle: Menor[]; brutoCentavos: number; pagan: number; ocupan: number; enPiernas: number; cortesias: number };
export function cotizarMenores(edades: number[], tramos: Tramo[], adultoCentavos: number): CotizacionMenores {
  const detalle = edades.map((e) => precioMenor(e, tramos, adultoCentavos));
  return {
    detalle, brutoCentavos: detalle.reduce((n, m) => n + m.centavos, 0), pagan: detalle.filter((m) => m.centavos > 0).length,
    ocupan: detalle.filter((m) => m.ocupaLugar).length, enPiernas: detalle.filter((m) => !m.ocupaLugar).length, cortesias: detalle.filter((m) => m.cortesia).length,
  };
}

/** Texto para el cliente y el vendedor: «Menores de 0 a 4 años: cortesía · 5 a 11: $400». */
export function describirTramos(tramos: Tramo[], adultoCentavos: number, moneda: (c: number) => string, es = true): string[] {
  const rango = (t: Tramo) => (t.desde === t.hasta ? `${t.desde}` : `${t.desde}–${t.hasta}`);
  const lineas = [...tramos].sort((a, b) => a.desde - b.desde).map((t) => {
    const m = precioMenor(t.desde, [t], adultoCentavos);
    const costo = m.cortesia ? (es ? "cortesía" : "free") : moneda(m.centavos);
    return `${es ? "De" : "Ages"} ${rango(t)} ${es ? "años" : ""}: ${costo}${m.cortesia && !t.ocupaLugar ? (es ? " (en piernas)" : " (on lap)") : ""}`.replace("  ", " ");
  });
  const tope = tramos.length ? Math.max(...tramos.map((t) => t.hasta)) : -1;
  if (tope >= 0 && tope < EDAD_MAXIMA) lineas.push(`${es ? `De ${tope + 1} años en adelante` : `Ages ${tope + 1}+`}: ${es ? "tarifa de adulto" : "adult fare"}`);
  return lineas;
}
